import type {ReactElement} from 'react';
import React, {useState, useEffect} from 'react';
import BaseNavOption from '@Components/poster-editor/components/poster-editor-nav-bar/components/base-nav-option/base-nav-option';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import {IconPlacement, Type} from '@Components/button';
import {onOpenDownloadModal} from '@Components/poster-editor/library/poster-editor-open-modals';
import {Icon} from '@Components/icon-v2';
import {DownloadPublishModalMode, NavRightSideOptionTypes} from '@Components/poster-editor/components/poster-editor-nav-bar/components/nav-right-options/nav-right-options.types';
import {openPosterEditorExportModal} from '@Modals/poster-editor-export-modal';
import {openUploadingImagesModal} from '@Modals/uploading-images-modal';
import {PublishingOptionsDropdown} from '@Panels/poster-publishing-options-panel/components/publishing-options-dropdown/publishing-options-dropdown';
import {useAppSelector} from '@/hooks';
import {usePosterHasMenuItem} from '@Hooks/poster-editor/usePosterHasMenuItem';
import {PublishingOptionsList} from '@Panels/poster-publishing-options-panel/components/publishing-options-default-list/publishing-options-default-list.types';
import {shouldShowRepublishButton} from '@Libraries/poster-library';

interface PublishRepublishButtonProps {
  type: NavRightSideOptionTypes;
  showPanelOnClick?: boolean;
}

export function PublishRepublishButton({showPanelOnClick = false, type = NavRightSideOptionTypes.BUTTON}: PublishRepublishButtonProps): ReactElement<any> | null {
  const hasUnsavedChanges = useAppSelector((state) => {
    return state.posterEditor.hasUnsavedChanges;
  });
  const isInitialized = useAppSelector((state) => {
    return state.posterEditor.posterObject?.isInitialized;
  });
  const areImagesUploading = useAppSelector((state) => {
    return !!state.posterEditor.posterObject?.uploadingImages.length;
  });
  const posterHasMenuItem = usePosterHasMenuItem();

  const [publishMode, setPublishMode] = useState(false);

  useEffect((): void => {
    if (hasUnsavedChanges && isInitialized) {
      setPublishMode(true);
    } else {
      setPublishMode(false);
    }
  }, [hasUnsavedChanges]);

  const openExportPanel = (): void => {
    openPosterEditorExportModal();
  };

  if (showPanelOnClick && isInitialized) {
    return <Icon onClick={openExportPanel} icon="icon-publish-arrow" size={IconSize.SIZE_ICON_20} type={IconType.GHOST} shape={IconShape.SQUARE} />;
  }

  const getOldPublishPanel = (): ReactElement<any> => {
    return (
      <BaseNavOption
        id="nav-publish-option"
        icon="icon-publish-arrow"
        btnClasses="publish-icon-hover-animation"
        iconPlacement={IconPlacement.RIGHT}
        text={window.i18next.t('pmwjs_publish')}
        optionType={type}
        iconType={IconType.GHOST}
        buttonType={Type.GHOST}
        isLoading={!isInitialized}
        onClickHandler={(): void => {
          onOpenDownloadModal(DownloadPublishModalMode.PUBLISH);
        }}
      />
    );
  };

  const getPublishDropDownForEditor = (): ReactElement<any> | null => {
    if (type === NavRightSideOptionTypes.HIDDEN) {
      return null;
    }

    return areImagesUploading ? (
      <BaseNavOption
        id="nav-publish-option"
        icon="icon-publish-arrow"
        btnClasses="publish-icon-hover-animation"
        iconPlacement={IconPlacement.RIGHT}
        text={window.i18next.t('pmwjs_publish')}
        optionType={type}
        iconType={IconType.GHOST}
        buttonType={Type.GHOST}
        isLoading={!isInitialized}
        onClickHandler={(): void => {
          const poster = window.posterEditor?.whiteboard;
          if (poster) {
            const imageSrc = poster.getImageSrcOfUploadingImage();

            if (imageSrc) {
              openUploadingImagesModal({
                title: window.i18next.t('pmwjs_uploading_images'),
                text: window.i18next.t('pmwjs_uploading_images_publishing_modal_description'),
                imgSrc: imageSrc,
              });
            }
          }
        }}
      />
    ) : (
      <PublishingOptionsDropdown
        hasUnsavedChanges={hasUnsavedChanges}
        publishMode={publishMode}
        isRepublishButton={shouldShowRepublishButton()}
        areImagesUploading={areImagesUploading}
        isInitialized={isInitialized}
        recommendedOptions={posterHasMenuItem ? [PublishingOptionsList.WEB_LINK, PublishingOptionsList.DIGITAL_SIGNAGE, PublishingOptionsList.QR_CODE] : []}
      />
    );
  };

  return window.PMW.showNewPublishDialog ? getPublishDropDownForEditor() : getOldPublishPanel();
}
